/**
 * The countertop over a SCRIBE. Pure.
 *
 * A leftover narrower than a filler can be (`MIN_FILLER_IN`, ½") is not
 * emitted as one: the boxes pack from the zone's start and the sliver stays at
 * its end, for the installer to scribe. Where that end dies into a WALL, the
 * top is templated to the wall on site — it does not stop short with a slot
 * of open counter beside the wall. So the slab runs on: one counter-only piece
 * from the outermost box to the wall, which `buildCountertopRuns` merges into
 * the box's slab (a piece that is not a `lower-cabinet` widens the slab and
 * adds no parent).
 *
 * Only at a `'wall'` end (`fillRun.fillZoneBounds`): an open end is a run that
 * is simply that much shorter, and a corner or a dishwasher end piece is
 * built against by another pass. Only from a BOX that is the last thing
 * before the wall — the strip from it to the zone's end must be narrower than
 * a filler, so an appliance or a tall bay's break standing there keeps the
 * slab where it was. The piece reaches the wall itself, not the zone's end:
 * a zone counts as ending AT the wall within the same filler's width.
 */

const { MIN_FILLER_IN } = require('./moduleSolver');

const IN = 0.0254;
const EPS = 1e-6;

/**
 * @param {Array<{wallId, range: {start, end}, type, depth?}>} counterItems
 *   the fill's counter-height pieces so far
 * @param {{ wallId: string, lo: number, hi: number, wallLen: number,
 *   ends: { lo: string, hi: string } }} zone one segment's fill zone, its
 *   wall's length and what stands at each end (`fillZoneBounds`)
 * @returns {Array<{wallId, range: {start, end}, type: 'scribe', depth?}>}
 */
function scribeCounterItems(counterItems, { wallId, lo, hi, wallLen, ends }) {
  const narrowest = MIN_FILLER_IN * IN;
  const inZone = (counterItems || []).filter(
    (i) =>
      i.wallId === wallId &&
      i.range &&
      i.range.start >= lo - EPS &&
      i.range.end <= hi + EPS
  );
  if (inZone.length === 0) return [];
  const out = [];
  const piece = (box, start, end) => ({
    wallId,
    range: { start, end },
    type: 'scribe',
    ...(box.depth != null ? { depth: box.depth } : {}),
  });

  if (ends?.hi === 'wall') {
    const last = inZone.reduce((a, b) => (b.range.end > a.range.end ? b : a));
    if (
      last.type === 'lower-cabinet' &&
      hi - last.range.end < narrowest &&
      wallLen - last.range.end > EPS
    ) {
      out.push(piece(last, last.range.end, wallLen));
    }
  }
  if (ends?.lo === 'wall') {
    const first = inZone.reduce((a, b) =>
      b.range.start < a.range.start ? b : a
    );
    if (
      first.type === 'lower-cabinet' &&
      first.range.start - lo < narrowest &&
      first.range.start > EPS
    ) {
      out.push(piece(first, 0, first.range.start));
    }
  }
  return out;
}

module.exports = { scribeCounterItems };
